import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  useGetProcurementCommentRoundsQuery,
  type CommentSubjectKind,
  type ProcurementCommentRoundsDto,
  type SubjectCommentDto,
} from "../../../../../../autogen/bff-api";

const CommentRoundContext = createContext<ProcurementCommentRoundsDto | undefined>(undefined);

interface LoadState {
  failed: boolean;
  isRetrying: boolean;
  retry: () => void;
}

const CommentRoundLoadContext = createContext<LoadState>({ failed: false, isRetrying: false, retry: () => undefined });

export const CommentRoundProvider = ({ eventId, children }: { eventId: string; children: ReactNode }) => {
  const { data, isError, isFetching, refetch } = useGetProcurementCommentRoundsQuery({ sourcingEventId: eventId });
  const load = useMemo(
    () => ({ failed: isError, isRetrying: isFetching, retry: () => void refetch() }),
    [isError, isFetching, refetch],
  );
  return (
    <CommentRoundLoadContext.Provider value={load}>
      <CommentRoundContext.Provider value={data}>{children}</CommentRoundContext.Provider>
    </CommentRoundLoadContext.Provider>
  );
};

export const useCommentRounds = () => useContext(CommentRoundContext);

export const useCommentRoundLoad = () => useContext(CommentRoundLoadContext);

const NO_COMMENTS: SubjectCommentDto[] = [];

export const useItemComments = (kind: CommentSubjectKind, ref: string | null | undefined): SubjectCommentDto[] => {
  const rounds = useCommentRounds();
  if (!rounds || ref == null) return NO_COMMENTS;
  return (rounds.comments ?? NO_COMMENTS).filter((comment) => comment.kind === kind && comment.ref === ref);
};
